import React, { Suspense, lazy, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { BACKEND_URL } from './config';

// ── Chrome desktop GLB texture fix ───────────────────────────────────────────
// Chrome's ImageBitmapLoader path uses fetch(blob:url) which requires
// connect-src blob: in CSP, and createImageBitmap() itself can fail for
// certain embedded JPEG/PNG formats in GLB files.
// Disabling createImageBitmap forces Three.js to use HTMLImageElement loading
// (img-src blob: is already allowed in CSP), which works in all browsers.
if (typeof window !== 'undefined') {
  window.createImageBitmap = undefined;
}
// ─────────────────────────────────────────────────────────────────────────────

// Lazy-load routes
const Login          = lazy(() => import('./Login'));
const Dashboard      = lazy(() => import('./Dashboard'));
const AdminDashboard = lazy(() => import('./AdminDashboard'));
const Docs           = lazy(() => import('./Docs'));
const Demo           = lazy(() => import('./Demo'));
const Live           = lazy(() => import('./Live'));
const Develop        = lazy(() => import('./Develop'));
const Stage          = lazy(() => import('./Stage'));
const Test           = lazy(() => import('./Test'));
const Debug          = lazy(() => import('./Debug'));
const Inspect        = lazy(() => import('./Inspect'));
const AvatarDebug    = lazy(() => import('./AvatarDebug'));
const AvatarAnimationDebug = lazy(() => import('./AvatarAnimationDebug'));
const TopicPlayer    = lazy(() => import('./TopicPlayer'));
const ScriptEditorPage = lazy(() => import('./ScriptEditorPage'));

// Wrapper that only renders Component if the session user is admin,
// otherwise redirects to / (not logged in) or /dashboard (wrong role).
function AdminOnly({ component: Component }) {
  const [status, setStatus] = useState('checking'); // checking | ok | denied

  useEffect(() => {
    fetch(`${BACKEND_URL}/auth/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d.authenticated && d.user.role === 'admin') {
          setStatus('ok');
        } else {
          setStatus('denied');
          window.location.href = d.authenticated ? '/dashboard' : '/';
        }
      })
      .catch(() => {
        setStatus('denied');
        window.location.href = '/';
      });
  }, []);

  if (status === 'checking') {
    return (
      <div style={{ width: '100vw', height: '100vh', background: '#080d1e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid rgba(99,102,241,0.2)', borderTopColor: '#6366f1' }} />
      </div>
    );
  }
  if (status !== 'ok') return null;
  return <Component />;
}

// Admin-only routes
const ADMIN_ONLY_PATHS = new Set([
  '/live', '/stage', '/develop',
  '/avatar-debug', '/avatar-animation-debug',
  '/test', '/debug', '/inspect',
]);

const AppRouter = () => {
  const path = window.location.pathname;
  let Component = Login; // default: login page
  let adminOnly = false;

  if (path.startsWith('/player'))              Component = TopicPlayer;
  else if (path.startsWith('/script-editor'))  Component = ScriptEditorPage;
  else if (path === '/dashboard')              Component = Dashboard;
  else if (path === '/admin')                  Component = AdminDashboard;
  else if (path === '/docs')                   Component = Docs;
  else if (path === '/demos')                  Component = Demo;
  else if (ADMIN_ONLY_PATHS.has(path)) {
    adminOnly = true;
    if (path === '/live')                   Component = Live;
    else if (path === '/stage')             Component = Stage;
    else if (path === '/develop')           Component = Develop;
    else if (path === '/avatar-debug')      Component = AvatarDebug;
    else if (path === '/avatar-animation-debug') Component = AvatarAnimationDebug;
    else if (path === '/test')              Component = Test;
    else if (path === '/debug')             Component = Debug;
    else if (path === '/inspect')           Component = Inspect;
  }

  const fallback = (
    <div style={{ width: '100vw', height: '100vh', background: '#000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      Loading...
    </div>
  );

  return (
    <Suspense fallback={fallback}>
      {adminOnly
        ? <AdminOnly component={Component} />
        : <Component />
      }
    </Suspense>
  );
};

ReactDOM.render(
  <React.StrictMode>
    <AppRouter />
  </React.StrictMode>,
  document.getElementById('root')
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
